import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Landmark, Trash2 } from 'lucide-react';
import { listarContas, criarConta, alternarConta, apagarConta } from '../../../data/recebimentos';

const TIPOS = [
  { id: 'corrente', label: 'Conta corrente' },
  { id: 'poupanca', label: 'Poupança' },
  { id: 'caixa',    label: 'Caixa / dinheiro' },
  { id: 'outro',    label: 'Outro' },
];

const VAZIO = { apelido: '', banco: '', tipo: 'corrente' };

/**
 * Contas onde o dinheiro entra.
 *
 * Lista fechada em vez de texto livre no recebimento, pelo mesmo motivo do
 * botijão e do touro: "Itaú", "itau" e "Itau PJ" digitados à mão viram três
 * contas diferentes no relatório, e aí a pergunta "quanto entrou no Itaú" não
 * tem resposta.
 */
const ContasBancarias = ({ laboratoryId, podeMexer = false }) => {
  const [contas, setContas] = useState([]);
  const [nova, setNova] = useState(VAZIO);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    try {
      setContas(await listarContas(laboratoryId) || []);
    } catch (e) {
      console.error('contas:', e); setErro(e.message);
    }
  }, [laboratoryId]);

  useEffect(() => { if (laboratoryId) carregar(); }, [laboratoryId, carregar]);

  const adicionar = async () => {
    if (!nova.apelido.trim()) return;
    setSalvando(true); setErro('');
    try {
      await criarConta({
        laboratory_id: laboratoryId,
        apelido: nova.apelido.trim(),
        banco: nova.banco.trim() || null,
        tipo: nova.tipo,
      });
      setNova(VAZIO);
      await carregar();
    } catch (e) {
      // 23505 é o índice de apelido único por laboratório.
      setErro(e.code === '23505'
        ? 'Já existe uma conta com esse apelido.'
        : e.message);
    } finally { setSalvando(false); }
  };

  const excluir = async (c) => {
    if (!window.confirm(
      `Excluir a conta "${c.apelido}"?\n\n`
      + 'Só é possível se nenhum recebimento apontar para ela. '
      + 'Para conta que deixou de existir, use Desativar — o histórico fica.')) return;
    try {
      await apagarConta(c.id);
      await carregar();
    } catch (e) {
      // 23503 = a FK RESTRICT de recebimentos segurou.
      setErro(e.code === '23503'
        ? 'Esta conta já tem recebimento lançado e por isso não pode ser excluída. Use Desativar.'
        : e.message);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="px-6 py-4 border-b flex items-center space-x-3">
        <Landmark className="text-slate-400" size={20} />
        <div>
          <h3 className="text-lg font-medium text-slate-900">Contas</h3>
          <p className="text-sm text-slate-600">
            Para onde o dinheiro vai. Aparecem ao lançar um recebimento.
          </p>
        </div>
      </div>

      {podeMexer && (
        <div className="px-6 py-4 border-b bg-slate-50">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input type="text" value={nova.apelido} placeholder="Apelido (Itaú PJ) *"
              onChange={(e) => setNova({ ...nova, apelido: e.target.value })}
              className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input type="text" value={nova.banco} placeholder="Banco (opcional)"
              onChange={(e) => setNova({ ...nova, banco: e.target.value })}
              className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select value={nova.tipo}
              onChange={(e) => setNova({ ...nova, tipo: e.target.value })}
              className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {TIPOS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>

          {erro && (
            <div className="mt-3 bg-white border border-slate-300 text-slate-700 text-sm rounded-lg px-3 py-2">
              {erro}
            </div>
          )}

          <button onClick={adicionar} disabled={!nova.apelido.trim() || salvando}
            className="mt-3 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm
                       hover:bg-blue-700 disabled:opacity-40">
            <Plus size={16} /> Cadastrar conta
          </button>
        </div>
      )}

      {contas.length === 0 ? (
        <div className="px-6 py-10 text-center">
          <Landmark className="mx-auto mb-3 text-slate-400" size={40} />
          <p className="text-slate-600 text-sm">Nenhuma conta cadastrada.</p>
          <p className="text-slate-400 text-xs mt-1">
            Sem conta, o recebimento é lançado sem dizer para onde o dinheiro foi.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {contas.map(c => (
            <div key={c.id} className={`px-6 py-3 flex items-center justify-between ${c.ativa ? '' : 'opacity-50'}`}>
              <div>
                <p className="font-medium text-slate-900 text-sm">
                  {c.apelido}
                  {!c.ativa && <span className="text-xs text-slate-500"> · inativa</span>}
                </p>
                <p className="text-xs text-slate-500">
                  {[c.banco, TIPOS.find(t => t.id === c.tipo)?.label].filter(Boolean).join(' · ')}
                </p>
              </div>
              {podeMexer && (
                <div className="flex items-center gap-1">
                  <button onClick={() => alternarConta(c.id, !c.ativa).then(carregar)}
                    className="text-slate-500 hover:text-slate-900 text-xs px-2 py-1 rounded hover:bg-slate-100">
                    {c.ativa ? 'Desativar' : 'Reativar'}
                  </button>
                  <button onClick={() => excluir(c)} title="Excluir conta"
                    className="text-slate-400 hover:text-slate-700 p-1 rounded hover:bg-slate-100">
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContasBancarias;
